import { useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { useAbortableLoader, isAbortError } from "@/hooks/useAbortableLoader";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/layouts/PageContainer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings as SettingsIcon, ChevronLeft, AlertCircle, Trash2, Lock, ShieldCheck } from "lucide-react";

const nativeSelect =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground";

export default function Settings() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [members, setMembers] = useState([]);

  const [name, setName] = useState("");
  const [sharingMode, setSharingMode] = useState("free");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [transferToUserId, setTransferToUserId] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, membersRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers, signal }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, { headers, signal }),
      ]);
      if (!schoolRes.ok) {
        const body = await schoolRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load school");
      }
      const data = await schoolRes.json();
      setSchool(data);
      setName(data.name);
      setSharingMode(data.sharingMode);
      if (membersRes.ok) setMembers(await membersRes.json());
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [schoolId, getToken]);

  useAbortableLoader(load);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: name.trim(), sharingMode }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to save settings");
      }
      const updated = await res.json();
      setSchool((prev) => ({ ...updated, isAdmin: prev?.isAdmin }));
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(
        `Delete "${school?.name}"? This cannot be undone. Report Folders will remain but will no longer be associated with this school.`
      )
    )
      return;
    setDeleting(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to delete school");
      }
      toast.success("School deleted.");
      navigate("/schools");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferToUserId) return;
    const recipient = members.find((m) => String(m.userId) === transferToUserId);
    const recipientName = recipient?.name || recipient?.email || "this teacher";
    if (
      !confirm(
        `Transfer admin role to ${recipientName}? They will need to accept. You will lose admin access once they accept.`
      )
    )
      return;
    setTransferring(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/admin-transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ toUserId: transferToUserId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to initiate transfer");
      }
      const updated = await res.json();
      setSchool((prev) => ({ ...prev, pendingAdminTransfer: updated.pendingAdminTransfer }));
      setTransferToUserId("");
      toast.success(`Transfer invitation sent to ${recipientName}.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTransferring(false);
    }
  };

  const handleRevoke = async () => {
    if (!confirm("Revoke the pending admin transfer? The invitation will be cancelled.")) return;
    setRevoking(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/admin-transfer`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to revoke transfer");
      }
      const updated = await res.json();
      setSchool((prev) => ({ ...prev, pendingAdminTransfer: updated.pendingAdminTransfer }));
      toast.success("Admin transfer revoked.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRevoking(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="h-64 bg-muted animate-pulse rounded-2xl" />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <SettingsIcon size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">School Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Update the school name and sharing policy.
          </p>
        </div>
      </div>

      <div className="max-w-[600px]">
      {/* Non-admin lock banner */}
      {!school?.isAdmin && (
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3.5 mb-6">
          <Lock size={15} className="text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">
              You are not permitted to change school settings
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Contact your school admin to update sharing settings or member permissions.
            </p>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-2xl shadow-sm">
        <form onSubmit={handleSave} className="px-6 py-6 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              School Name <span className="text-destructive/80 font-normal">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Class Sharing Mode</Label>
            <select
              value={sharingMode}
              onChange={(e) => setSharingMode(e.target.value)}
              className={nativeSelect}
            >
              <option value="free">Free - teachers share Report Folders directly</option>
              <option value="restricted">
                Restricted - admin approves all share requests
              </option>
            </select>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {sharingMode === "free"
                ? "Teachers can share Report Folders with each other without approval."
                : "Every share request must be approved by you before access is granted."}
            </p>
          </div>

        
          {school?.isAdmin && (
            <div className="flex gap-2 pt-1">
              <Button type="submit" disabled={saving || !name.trim()} className="flex-1">
                {saving ? "Saving..." : "Save Settings"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/schools")}
              >
                Cancel
              </Button>
            </div>
          )}
        </form>
      </div>

      {/* Admin transfer - admin only */}
      {school?.isAdmin && (
      <div className="mt-6 bg-card border border-border rounded-2xl px-6 py-5">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={14} className="text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Transfer Admin Role</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Transfer the admin role to another teacher in this school. They will receive an
          invitation and must accept before the transfer takes effect.
        </p>

        {school?.pendingAdminTransfer?.toUserId && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5 mb-4 flex-wrap gap-y-2">
            <div className="flex items-center gap-2 text-xs">
              <ShieldCheck size={13} className="text-primary shrink-0" />
              <span>
                Pending transfer to{" "}
                <span className="font-medium text-foreground">
                  {members.find(
                    (m) => String(m.userId) === String(school.pendingAdminTransfer.toUserId)
                  )?.name || "a teacher"}
                </span>
                . Waiting for acceptance.
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30 shrink-0"
              disabled={revoking}
              onClick={handleRevoke}
            >
              {revoking ? "Revoking..." : "Revoke"}
            </Button>
          </div>
        )}

        {members.filter((m) => !m.isAdmin).length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            No other members to transfer to.
          </p>
        ) : (
          <form onSubmit={handleTransfer} className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1.5 flex-1 min-w-[200px]">
              <Label className="text-xs font-medium">Transfer to</Label>
              <select
                className={nativeSelect}
                value={transferToUserId}
                onChange={(e) => setTransferToUserId(e.target.value)}
                required
              >
                <option value="">- Select a teacher -</option>
                {members
                  .filter((m) => !m.isAdmin)
                  .map((m) => (
                    <option key={String(m.userId)} value={String(m.userId)}>
                      {m.name || m.email}
                    </option>
                  ))}
              </select>
            </div>
            <Button
              type="submit"
              variant="outline"
              size="sm"
              disabled={transferring || !transferToUserId}
              className="h-10"
            >
              {transferring ? "Sending..." : "Transfer Role"}
            </Button>
          </form>
        )}
      </div>
      )}

      {/* Danger zone - admin only */}
      {school?.isAdmin && (
      <div className="mt-6 bg-card border border-destructive/30 rounded-2xl px-6 py-5">
        <h2 className="text-sm font-semibold text-destructive mb-1">Danger Zone</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Deleting a school is permanent. Members will lose access but existing Report Folder data
          will be preserved. This action cannot be undone.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
          onClick={handleDelete}
          disabled={deleting}
        >
          <Trash2 size={14} />
          {deleting ? "Deleting..." : "Delete School"}
        </Button>
      </div>
      )}
      </div>
    </PageContainer>
  );
}
